"use client";

import { useEffect, useState, type ReactNode } from "react";
import Lenis from "lenis";
import { gsap, ScrollTrigger } from "@/lib/gsap";
import { INTRO_COMPLETE_EVENT, INTRO_TOTAL, LenisContext } from "@/lib/lenis-context";

/**
 * Lenis drives the page scroll; GSAP's ticker drives Lenis. One clock means ScrollTrigger
 * scrubs, parallax and the smooth scroll all advance on the exact same frame — no jitter
 * between scrolled content and scroll-linked animation.
 */
export function SmoothScroll({ children }: { children: ReactNode }) {
  const [lenis, setLenis] = useState<Lenis | null>(null);

  useEffect(() => {
    const root = document.documentElement;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if ("scrollRestoration" in history) history.scrollRestoration = "manual";

    const instance = new Lenis({
      // Low lerp = heavier, more "expensive" glide. Touch keeps native momentum.
      lerp: reduce ? 1 : 0.082,
      smoothWheel: !reduce,
      wheelMultiplier: 0.9,
      touchMultiplier: 1.3,
      syncTouch: false,
      // Lenis's own `anchors` handling is deliberately not used: it puts the fragment in the
      // address bar. The listener below scrolls to the same place and leaves the URL alone.
      autoRaf: false,
    });

    /* In-page links scroll; they do not rewrite the address bar.
     *
     * "Book a demo" on a product page points at `#demo` on that same page. Left to the
     * browser, every click turns /finance into /finance#demo — the URL stops naming the page
     * the visitor thinks they are on, each click adds a history entry, and Back then walks
     * through scroll positions instead of going back to the previous page.
     *
     * So we scroll to the target ourselves and never touch the URL. Only plain left-clicks on
     * a same-page fragment that actually resolves are taken; a modified click, a new-tab
     * target, a download, a bare "#", or a fragment with no matching element all fall through
     * to the browser untouched. */
    const ANCHOR_OFFSET = -84;
    const onAnchorClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const link = (event.target as Element | null)?.closest?.("a");
      if (!link) return;
      if (link.hasAttribute("download")) return;
      const linkTarget = link.getAttribute("target");
      if (linkTarget && linkTarget !== "_self") return;

      const href = link.getAttribute("href");
      if (!href || href === "#" || !href.startsWith("#")) return;

      const el = document.getElementById(decodeURIComponent(href.slice(1)));
      if (!el) return;

      event.preventDefault();
      instance.scrollTo(el, { offset: ANCHOR_OFFSET });
    };
    document.addEventListener("click", onAnchorClick);

    instance.on("scroll", ScrollTrigger.update);
    const tick = (time: number) => instance.raf(time * 1000);
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(0);

    /* A hash that arrives WITH the page — a cross-page link such as "/#pricing" from the nav
     * on any other page — never lands on its own here. Lenis's `anchors` option covers anchor
     * CLICKS only, and the browser's own jump is cancelled twice over: by scrollRestoration
     * "manual" above, and by the hero holding scroll while its entrance plays. The result was a
     * visitor dropped at the top of the homepage with "#pricing" in the address bar. So make
     * the jump here, once scrolling is actually live. */
    const hash = window.location.hash;
    const incoming = hash.length > 1 ? document.getElementById(decodeURIComponent(hash.slice(1))) : null;
    // A fragment pointing at the very top of the document says nothing the bare path does not
    // already say, so it goes immediately rather than being landed on and cleaned up later.
    // "/#top" links are no longer generated, but shared and bookmarked ones still arrive.
    const atDocumentTop = !!incoming && incoming.getBoundingClientRect().top + window.scrollY < 8;
    if (atDocumentTop) {
      history.replaceState(null, "", window.location.pathname + window.location.search);
    }
    const target = atDocumentTop ? null : incoming;
    const landOnHash = () => {
      if (target) instance.scrollTo(target, { offset: ANCHOR_OFFSET, immediate: true, force: true });
    };

    /* Back, Forward and reload return to where the visitor left the page. Every link here is a
     * full page load and scrollRestoration is "manual" (above), so each Back used to drop the
     * visitor at the top of the page they came back to. Positions are kept per URL for the
     * tab's session, written as the page is left. A hash landing takes precedence. */
    const scrollKey = `quikit:scroll:${window.location.pathname}${window.location.search}`;
    const saveScroll = () => {
      try {
        sessionStorage.setItem(scrollKey, String(Math.round(window.scrollY)));
      } catch {
        // Storage blocked: Back simply lands at the top, as it did before.
      }
    };
    window.addEventListener("pagehide", saveScroll);
    const navType = (performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined)?.type;
    let restoreY = 0;
    if (!target && (navType === "back_forward" || navType === "reload")) {
      try {
        restoreY = Number(sessionStorage.getItem(scrollKey)) || 0;
      } catch {
        // As above.
      }
    }

    // The hero locks scrolling while its entrance plays; release on its signal (or a safety net).
    // Idempotent: the signal and the safety net can both arrive, and a second landing would
    // yank back a visitor who has already started scrolling.
    let released = false;
    const release = () => {
      if (released) return;
      released = true;
      root.dataset.introDone = "1";
      instance.start();
      landOnHash();
    };
    let safety: number | undefined;
    if (!root.dataset.introDone && !reduce) {
      instance.stop();
      safety = window.setTimeout(release, INTRO_TOTAL * 1000 + 600);
    } else {
      // Nothing is holding scroll — land on the next frame, once layout exists.
      requestAnimationFrame(landOnHash);
    }
    window.addEventListener(INTRO_COMPLETE_EVENT, release);

    // Layout settles after webfonts swap in — re-measure every trigger once they're ready. A
    // restored position is applied after that, against the settled page height.
    // Only from the very top, though: a page that is already scrolled here was put there by the
    // browser's own restoration (which is per history entry, so more precise than this per-URL
    // record) or by the visitor scrolling before hydration — and either way must not be yanked.
    document.fonts?.ready.then(() => {
      ScrollTrigger.refresh();
      if (restoreY && window.scrollY < 2) instance.scrollTo(restoreY, { immediate: true, force: true });
    });

    /* Then keep the address bar honest. Once the visitor scrolls away under their own steam,
     * the hash no longer describes where they are — and it is the history entry too, so coming
     * back to this page later would land on "/#pricing" rather than where they actually were.
     *
     * It is dropped on the visitor's own first scroll input — a wheel, a touch drag or a scroll
     * key, none of which fire for the programmatic landing above — and only once that landing
     * has happened. Someone who never scrolls away keeps the hash, which is correct: it still
     * describes where they are. replaceState neither moves the page nor adds a history entry. */
    let dropHash: (() => void) | undefined;
    if (target) {
      let landed = false;
      // The anchor offset puts the target just under the nav rather than at 0, so this is a
      // tolerance band, not an equality test.
      const watch = () => {
        if (Math.abs(target.getBoundingClientRect().top) < 160) landed = true;
      };
      const drop = () => {
        if (!landed) return;
        history.replaceState(null, "", window.location.pathname + window.location.search);
        dropHash?.();
      };
      dropHash = () => {
        instance.off("scroll", watch);
        window.removeEventListener("wheel", drop);
        window.removeEventListener("touchmove", drop);
        window.removeEventListener("keydown", drop);
        dropHash = undefined;
      };
      instance.on("scroll", watch);
      window.addEventListener("wheel", drop, { passive: true });
      window.addEventListener("touchmove", drop, { passive: true });
      window.addEventListener("keydown", drop);
    }

    setLenis(instance);

    return () => {
      document.removeEventListener("click", onAnchorClick);
      window.removeEventListener("pagehide", saveScroll);
      dropHash?.();
      window.clearTimeout(safety);
      window.removeEventListener(INTRO_COMPLETE_EVENT, release);
      gsap.ticker.remove(tick);
      instance.destroy();
      setLenis(null);
    };
  }, []);

  return <LenisContext.Provider value={lenis}>{children}</LenisContext.Provider>;
}
